import { useState } from "react";
import { siteInfo } from "@/mocks/site";

const FORM_ID = "kanliao88-subscribe";
const FORM_URL = "https://readdy.ai/api/form/dailrc3p14a1h3mr76k0";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const payload = new FormData(formEl);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    payload.delete("website_alt");
    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const looksLikeSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !looksLikeSpam) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <section className="bg-background-100" aria-labelledby="subscribe-title">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-mail-send-line" />
              每周一封
            </span>
            <h2 id="subscribe-title" className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              订阅热点周报，不错过任何一条后续
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              每周一发送当周热度最高的十条话题和各自的最新进展，一周的瓜一片看完，不发广告。
            </p>
            <div className="mt-6 flex flex-wrap gap-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-secondary-600" />
                每周一封，随时可退订
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-check-line text-secondary-600" />
                仅发送内容汇总
              </span>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3"
            >
              <div>
                <label htmlFor={`${FORM_ID}-email`} className="mb-1.5 block text-xs font-medium text-foreground-700">
                  邮箱地址
                </label>
                <input
                  id={`${FORM_ID}-email`}
                  name="email"
                  type="email"
                  required
                  maxLength={120}
                  placeholder="you@example.com"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label htmlFor={`${FORM_ID}-interest`} className="mb-1.5 block text-xs font-medium text-foreground-700">
                  最想看的方向
                </label>
                <select
                  id={`${FORM_ID}-interest`}
                  name="interest"
                  defaultValue="全部热点"
                  className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option>全部热点</option>
                  <option>综艺现场</option>
                  <option>影视风向</option>
                  <option>音乐现场</option>
                  <option>网络话题</option>
                  <option>时尚潮流</option>
                  <option>赛事娱乐</option>
                </select>
              </div>

              <div className="extra-field-note">
                <label htmlFor={`${FORM_ID}-alt`}>备注</label>
                <input
                  id={`${FORM_ID}-alt`}
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-notification-3-line" />
                    免费订阅周报
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="flex items-center gap-1.5 text-xs text-secondary-700">
                  <i className="ri-checkbox-circle-line" />
                  订阅成功，下周一开始你会收到第一封周报。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-1.5 text-xs text-primary-700">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}
              <p className="text-xs text-foreground-400">
                我们仅存储你的邮箱用于发送周报，不会用于其他用途。如需联系编辑，也可发信至 {siteInfo.email}。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}